import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
// React 19 native metadata
import { Swords, Copy, Check, ExternalLink, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BattlePage = () => {
  const { battleId } = useParams();
  const [battle, setBattle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/roast/battle/${battleId}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => setBattle(d))
      .catch(() => setError('Battle not found'))
      .finally(() => setLoading(false));
  }, [battleId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="text-gray-400 animate-pulse">Loading battle report...</div>
    </div>
  );

  if (error || !battle) return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center gap-4">
      <p className="text-gray-400">Battle not found</p>
      <Link to="/" className="text-purple-400 hover:text-purple-300">Back to iSupplyAI</Link>
    </div>
  );

  const summary = battle.summary || {};
  const insights = battle.insights || [];
  const scoreColor = summary.battle_score >= 7 ? 'text-green-400' : summary.battle_score >= 4 ? 'text-amber-400' : 'text-red-400';
  const scoreLabel = summary.battle_score >= 7 ? 'Very Beatable' : summary.battle_score >= 4 ? 'Competitive Fight' : 'Tough Opponent';

  // Smart-truncate battle title for SEO (keep under 60 chars for SERP)
  const battleTitle = (() => {
    const y = (battle.your_title || battle.your_url || 'Site A').replace(/^https?:\/\/(www\.)?/, '').slice(0, 18);
    const c = (battle.competitor_title || battle.competitor_url || 'Site B').replace(/^https?:\/\/(www\.)?/, '').slice(0, 18);
    return `${y} vs ${c} Battle Report | iSupplyAI`;
  })();

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <>
        <title>{battleTitle.length > 60 ? battleTitle.slice(0, 60) : battleTitle}</title>
        <meta name="description" content={`AI competitive battle analysis: ${battle.your_title || battle.your_url} vs ${battle.competitor_title || battle.competitor_url}. Battle score: ${summary.battle_score}/10.`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={`Battle Report: ${battle.your_title || battle.your_url} vs ${battle.competitor_title || battle.competitor_url} | iSupplyAI`} />
        <meta property="og:description" content={`AI competitive battle analysis: ${battle.your_title || battle.your_url} vs ${battle.competitor_title || battle.competitor_url}. Battle score: ${summary.battle_score}/10.`} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content={`${battle.your_title || battle.your_url} vs ${battle.competitor_title || battle.competitor_url} | iSupplyAI`} />
        <meta name="twitter:description" content={`AI competitive battle analysis. Battle score: ${summary.battle_score}/10.`} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
      </>

      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 bg-[#0a0a0f]/95 backdrop-blur-md z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="battle-home-link">
            <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI" className="h-8 w-8 object-contain" />
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">iSupplyAI</span>
          </Link>
          <button onClick={handleCopy} data-testid="battle-copy-link" className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10">
        {/* Title */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Swords className="w-6 h-6 text-red-400" />
            <p className="text-sm font-semibold tracking-widest text-red-400 uppercase">Battle Report</p>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="battle-title">
            <span className="text-purple-300">{battle.your_title || battle.your_url}</span>
            <span className="text-gray-500 mx-3">vs</span>
            <span className="text-red-300">{battle.competitor_title || battle.competitor_url}</span>
          </h1>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <a href={battle.your_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-purple-400 transition-colors">
              <ExternalLink className="w-3 h-3" /> {battle.your_url}
            </a>
            <span>vs</span>
            <a href={battle.competitor_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-red-400 transition-colors">
              <ExternalLink className="w-3 h-3" /> {battle.competitor_url}
            </a>
          </div>
        </div>

        {/* Battle Score */}
        <div className="bg-gradient-to-r from-red-900/30 to-amber-900/20 border border-red-500/20 rounded-2xl p-8 mb-10" data-testid="battle-score-card">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400 mb-1">Beatability Score</p>
              <div className="flex items-baseline gap-2">
                <span className={`text-6xl font-black ${scoreColor}`}>{summary.battle_score}</span>
                <span className="text-2xl text-gray-500">/10</span>
              </div>
              <span className={`text-sm font-semibold ${scoreColor}`}>{scoreLabel}</span>
            </div>
            <div className="flex-1 max-w-xl">
              <p className="text-lg text-white font-medium">{summary.battle_assessment}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="bg-black/30 rounded-xl p-5">
              <p className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-2">Biggest Vulnerability</p>
              <p className="text-gray-200">{summary.biggest_vulnerability}</p>
            </div>
            <div className="bg-black/30 rounded-xl p-5">
              <p className="text-xs text-amber-400 font-semibold uppercase tracking-wider mb-2">#1 Priority</p>
              <p className="text-gray-200">{summary.top_priority}</p>
            </div>
          </div>

          {summary.kill_shots?.length > 0 && (
            <div className="mt-4 bg-black/30 rounded-xl p-5">
              <p className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-3">Kill Shots — Do This Week</p>
              <div className="space-y-2">
                {summary.kill_shots.map((shot, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-red-400 text-lg mt-0.5">&#9876;</span>
                    <p className="text-gray-200">{shot}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* God Insights */}
        <h2 className="text-2xl font-bold mb-6">God-by-God Battle Analysis</h2>
        <div className="space-y-5 mb-12">
          {insights.filter(i => !i.error).map((insight) => (
            <div key={insight.god} className="bg-white/[0.03] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors" data-testid={`battle-god-${insight.god}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{insight.emoji}</span>
                <div>
                  <p className="font-bold text-white text-lg">{insight.name}</p>
                  <p className="text-xs text-gray-500">{insight.title} — {insight.focus}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-4">
                  <p className="text-xs text-red-400 font-semibold uppercase mb-2">Their Weakness</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{insight.weakness}</p>
                </div>
                <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-4">
                  <p className="text-xs text-green-400 font-semibold uppercase mb-2">Your Advantage</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{insight.advantage}</p>
                </div>
              </div>
              <div className="mt-4 bg-amber-900/20 border border-amber-800/30 rounded-lg p-4">
                <p className="text-xs text-amber-400 font-semibold uppercase mb-2">Attack Plan</p>
                <p className="text-gray-300 text-sm leading-relaxed">{insight.attack_plan}</p>
              </div>
              {insight.kill_shot && (
                <div className="mt-3 flex items-start gap-2 px-4">
                  <span className="text-red-400 font-bold text-sm shrink-0">Kill Shot:</span>
                  <span className="text-gray-300 text-sm">{insight.kill_shot}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center p-10 bg-gradient-to-r from-purple-900/30 to-cyan-900/20 border border-purple-500/20 rounded-2xl">
          <Swords className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">Run Your Own Battle</h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto">Enter your website and any competitor. 7 AI gods will analyze both sites and give you a data-driven attack plan to beat them.</p>
          <Link
            to="/"
            data-testid="battle-cta-btn"
            className="inline-block px-8 py-3 bg-gradient-to-r from-red-600 to-amber-500 text-white font-bold rounded-full hover:shadow-lg hover:shadow-red-500/25 transition-all"
          >
            Beat My Competitor — Free
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 mt-12">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} iSupplyAI, LLC</p>
          <div className="flex gap-6">
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BattlePage;
